import React, { useContext, useState } from 'react';
import { Layout } from 'react-grid-layout';
import {
  DASHBOARD_PREFERENCE_MODEL,
  ORG_DASHBOARD_MODEL,
} from '../../general/vol-dashboard/vol-dashboard-my-elements/management-dashboard/dashboard-widget-setting/dashboard-preference-model.gql';
import { GetOrgDashboard } from '../../general/vol-dashboard/vol-dashboard-my-elements/management-dashboard/dashboard-widget-setting/__generated__/GetOrgDashboard';
import {
  GetDashboardPreference,
  GetDashboardPreferenceVariables,
  GetDashboardPreference_vm_profile_dashboardPreference_dashboardComponents as IDashboardComponent,
} from '../../general/vol-dashboard/vol-dashboard-my-elements/management-dashboard/dashboard-widget-setting/__generated__/GetDashboardPreference';
import { useCampfireQuery } from '../../../global/network/useCampfireQuery';
import { useUser } from '../../../global/auth/useUser';
import {
  useSaveWidget,
  useSaveWidgetLayout,
} from '../../general/vol-dashboard/vol-dashboard-my-elements/management-dashboard/dashboard-widget-setting/useSaveWidget';
import { useDeleteWidget } from '../../general/vol-dashboard/vol-dashboard-my-elements/management-dashboard/dashboard-widget-setting/useDeleteWidget';
import {
  CreatWidgetFormValues,
  WidgetTypes,
} from '../../general/vol-dashboard/vol-dashboard-my-elements/management-dashboard/dashboard-widget-setting/dashboard-widget-model';
import { useOrgInfo } from '../../../global/auth/use-org-info/use-org-info';
import { PWI_TENANT_IDS } from '../../general/vol-dashboard/vol-dashboard-my-elements/management-dashboard/widgets/pwi-widget/pwi-results';
import { useDeepEffect } from '../../../hooks/useDeepEffect';
import { CreateWidgetDialog } from '../../general/vol-dashboard/vol-dashboard-my-elements/management-dashboard/dashboard-widget-setting/CreateWidgetDialog';

export type DashboardView = 'mine' | 'organisation';

interface ManagementDashboardContextInterface {
  view: DashboardView;
  setView: (view: DashboardView) => void;
  canEdit: boolean;
  personalDashboardsDisabled: boolean;
  showEditWidget: boolean;
  setShowEditWidget: React.Dispatch<React.SetStateAction<boolean>>;
  components: IDashboardComponent[];
  layouts: Layout[];
  onLayoutChange: (values: Layout[]) => void;
  onSubmitLayout: () => void;
  onCancelChangeLayout: () => void;
  onDeleteWidget: (id: string) => void;
  onEditWidget: (c: IDashboardComponent) => void;
  editLayout: boolean;
  setEditLayout: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
  selectedWidget?: IDashboardComponent;
  setSelectedWidget: (c: IDashboardComponent) => void;
}

interface ManagementDashboardProviderProps {
  children: React.ReactNode;
}

export const ManagementDashboardContext = React.createContext<ManagementDashboardContextInterface>({
  view: 'mine',
  setView: () => {},
  canEdit: true,
  personalDashboardsDisabled: false,
  showEditWidget: false,
  setShowEditWidget: () => {},
  components: [] as IDashboardComponent[],
  layouts: [] as Layout[],
  onLayoutChange: () => {},
  onSubmitLayout: () => {},
  onCancelChangeLayout: () => {},
  onDeleteWidget: () => {},
  onEditWidget: () => {},
  editLayout: false,
  setEditLayout: () => {},
  loading: false,
  selectedWidget: undefined,
  setSelectedWidget: () => {},
});

const ManagementDashboardProvider = (props: ManagementDashboardProviderProps) => {
  const [showEditWidget, setShowEditWidget] = useState<boolean>(false);
  const [editLayout, setEditLayout] = useState<boolean>(false);
  const [selectedWidget, setSelectedWidget] = useState<IDashboardComponent>();
  const [view, setViewState] = useState<DashboardView>('mine');

  const { user } = useUser();
  const orgInfo = useOrgInfo();
  const { userId } = user;

  const { data: vmProfile, refetch: refetchMine, loading: loadingMine } = useCampfireQuery<
    GetDashboardPreference,
    GetDashboardPreferenceVariables
  >(DASHBOARD_PREFERENCE_MODEL, { options: { variables: { userId } } });

  const { data: orgData, refetch: refetchOrg, loading: loadingOrg } = useCampfireQuery<GetOrgDashboard, {}>(
    ORG_DASHBOARD_MODEL
  );

  const personalDashboardsDisabled = Boolean(orgData?.vm.personalDashboardsDisabled);
  const isOrg = personalDashboardsDisabled || view === 'organisation';
  const effectiveView: DashboardView = isOrg ? 'organisation' : view;
  const canEdit = isOrg ? Boolean(orgData?.vm.canEditOrgDashboard) : true;
  const loading = isOrg ? loadingOrg : loadingMine;
  const refetchProfile = isOrg ? refetchOrg : refetchMine;

  const components =
    (isOrg
      ? orgData?.vm.orgDashboard?.dashboardComponents
      : vmProfile?.vm.profile?.dashboardPreference?.dashboardComponents) || [];

  const setView = (nextView: DashboardView) => {
    setEditLayout(false);
    setViewState(nextView);
  };

  const [layouts, setLayouts] = React.useState<Layout[]>(
    components.map((dashBoardComponent) => ({
      i: dashBoardComponent.dashboardComponentId,
      x: dashBoardComponent.position.row,
      y: dashBoardComponent.position.col,
      h: dashBoardComponent.position.rowSpan,
      w: dashBoardComponent.position.colSpan,
    }))
  );

  const saveWidget = useSaveWidget();
  const saveWidgetLayout = useSaveWidgetLayout();
  const removeWidget = useDeleteWidget();

  const getNextItem = () => {
    const maxY = Math.max(...layouts.map(({ y }) => y));
    const lastItem = layouts.filter((l) => l.y === maxY).sort((a, b) => (a.x > b.x ? -1 : 1))[0];
    return lastItem
      ? lastItem.x + lastItem.w < 4
        ? { x: lastItem.x + lastItem.w, y: maxY }
        : { x: 0, y: maxY + lastItem.h }
      : { x: 0, y: 0 };
  };

  const onUpdateWidget = (values: CreatWidgetFormValues) => {
    const nextItem = getNextItem();

    saveWidget(
      values,
      {
        row: nextItem.x,
        col: nextItem.y,
        rowSpan: 1,
        colSpan: values.byType === WidgetTypes.PWI ? 2 : 1,
      },
      selectedWidget?.dashboardComponentId,
      isOrg
    ).then(() => {
      if (refetchProfile) {
        refetchProfile();
      }
      handleCloseEditDialog();
    });
  };

  const onLayoutChange = (newLayouts: Layout[]) => {
    if (editLayout) {
      setLayouts(newLayouts);
    }
  };

  useDeepEffect(() => {
    setLayouts(
      components.map((dashBoardComponent) => ({
        i: dashBoardComponent.dashboardComponentId,
        x: dashBoardComponent.position.row,
        y: dashBoardComponent.position.col,
        h: dashBoardComponent.position.rowSpan,
        w: dashBoardComponent.position.colSpan,
      }))
    );
  }, [components]);

  const onCancelChangeLayout = () => {
    setLayouts(
      components.map((dashBoardComponent) => ({
        i: dashBoardComponent.dashboardComponentId,
        x: dashBoardComponent.position.row,
        y: dashBoardComponent.position.col,
        h: dashBoardComponent.position.rowSpan,
        w: dashBoardComponent.position.colSpan,
      }))
    );
    setEditLayout(false);
  };

  const onSubmitLayout = () => {
    Promise.all(
      layouts.map((layout) => {
        return saveWidgetLayout(
          layout.i,
          {
            row: layout.x,
            col: layout.y,
            rowSpan: layout.h,
            colSpan: layout.w,
          },
          isOrg
        );
      })
    ).then(() => {
      if (refetchProfile) {
        refetchProfile();
      }
      setEditLayout(false);
    });
  };

  const onEditWidget = (widget: IDashboardComponent) => {
    setSelectedWidget(widget);
    setShowEditWidget(true);
  };

  const onDeleteWidget = (dashboardComponentId: string) => {
    removeWidget({ dashboardComponentId, orgDashboard: isOrg }).then(() => {
      if (refetchProfile) {
        refetchProfile();
      }
    });
  };

  const handleCloseEditDialog = () => {
    setShowEditWidget(false);
    setSelectedWidget(undefined);
  };

  const value = {
    view: effectiveView,
    personalDashboardsDisabled,
    setView,
    canEdit,
    showEditWidget,
    setShowEditWidget,
    layouts: layouts,
    onLayoutChange,
    components,
    onCancelChangeLayout,
    onSubmitLayout,
    onDeleteWidget,
    onEditWidget,
    editLayout,
    setEditLayout,
    loading,
    selectedWidget,
    setSelectedWidget,
  };

  return (
    <ManagementDashboardContext.Provider value={value} {...props}>
      {props.children}
      {showEditWidget && (
        <CreateWidgetDialog
          selectedWidget={selectedWidget}
          open={showEditWidget}
          onClose={handleCloseEditDialog}
          handleSubmit={onUpdateWidget}
          allowPwi={isOrg && PWI_TENANT_IDS.includes(orgInfo?.tenantId ?? '')}
          isOrgDashboard={isOrg}
        />
      )}
    </ManagementDashboardContext.Provider>
  );
};

export const useManagementDashboardContext = () => {
  const value = useContext(ManagementDashboardContext);
  if (!value) throw new Error('Must be inside of a ManagementDashboardProvider');
  return value;
};

export { ManagementDashboardProvider };
